/**
 * FeatureAccessContext
 *
 * Central React context for slug-based plan & feature management.
 * Fetches the feature map once from `/api/features` and exposes
 * helper methods that mirror the backend FeatureAccessService:
 *
 *   - hasFeature(key)            -> boolean
 *   - getFeatureValue(key)       -> boolean | number | string | null
 *   - getFeatureLimit(key)       -> number (-1 = unlimited, 0 = disabled)
 *   - getFeatureText(key)        -> string | null
 *   - featureValueContains(key, option) -> boolean
 *   - featureLimitReached(key, current) -> boolean
 *   - getRequiredPlan(key)      -> { id, key, name, price } | null
 *   - showUpgradeModal(key)     -> opens the reusable upgrade modal
 *
 * Feature keys (slugs) are the single source of truth - no hardcoded names.
 */

import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    type ReactNode,
} from 'react';
import { apiGet } from '../lib/api';
import { useAuth } from './AuthContext';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export interface PlanSummary {
    id: number;
    key: string;
    name: string;
    price: number;
}

/** A feature value can be boolean (checkbox), number (numeric), or string (text). */
export type FeatureValue = boolean | number | string | null;

export interface FeatureMap {
    [featureKey: string]: FeatureValue;
}

export interface RequiredPlanInfo {
    id: number;
    key: string;
    name: string;
    price: number;
}

export interface FeatureCheckResponse {
    success: boolean;
    feature: string;
    enabled: boolean;
    value: FeatureValue;
    type: string | null;
    required_plan: RequiredPlanInfo | null;
}

export interface FeatureLimitResponse {
    success: boolean;
    feature: string;
    limit: number;
    current: number;
    limit_reached: boolean;
    unlimited: boolean;
}

export interface FeatureContainsResponse {
    success: boolean;
    feature: string;
    option: string;
    contains: boolean;
    value: string | null;
}

export interface RequiredPlanResponse {
    success: boolean;
    feature: string;
    required_plan: RequiredPlanInfo | null;
}

interface FeatureAccessContextValue {
    /** The active plan summary (id, key, name, price). */
    plan: PlanSummary | null;
    /** Flat map of featureKey => value for the active plan. */
    features: FeatureMap;
    /** True while the feature map is being loaded. */
    loading: boolean;
    /** Error message if the feature map failed to load. */
    error: string | null;
    /** Manually re-fetch the feature map (e.g. after plan change). */
    refresh: () => Promise<void>;

    /* ── Feature checks ──────────────────────────────────────────── */
    hasFeature: (key: string) => boolean;
    getFeatureValue: (key: string) => FeatureValue;
    getFeatureLimit: (key: string) => number;
    getFeatureText: (key: string) => string | null;
    featureValueContains: (key: string, option: string) => boolean;
    featureLimitReached: (key: string, currentCount: number) => boolean;

    /* ── Upgrade modal ───────────────────────────────────────────── */
    /** Open the reusable upgrade modal for a given feature key. */
    showUpgradeModal: (featureKey: string, featureLabel?: string) => void;
    /** Close the upgrade modal. */
    closeUpgradeModal: () => void;
    /** Current upgrade-modal state (consumed by the modal component). */
    upgradeModal: {
        open: boolean;
        featureKey: string | null;
        featureLabel: string | null;
        requiredPlan: RequiredPlanInfo | null;
    };

    /* ── Server-side lookups (for authoritative checks) ───────────── */
    checkFeature: (key: string) => Promise<FeatureCheckResponse>;
    checkLimit: (key: string, current: number) => Promise<FeatureLimitResponse>;
    checkContains: (key: string, option: string) => Promise<FeatureContainsResponse>;
    fetchRequiredPlan: (key: string) => Promise<RequiredPlanResponse>;
}

/* ------------------------------------------------------------------ */
/* Context                                                            */
/* ------------------------------------------------------------------ */

const FeatureAccessContext = createContext<FeatureAccessContextValue | undefined>(undefined);

/* ------------------------------------------------------------------ */
/* Provider                                                           */
/* ------------------------------------------------------------------ */

interface FeatureAccessProviderProps {
    children: ReactNode;
}

export function FeatureAccessProvider({ children }: FeatureAccessProviderProps) {
    const { token, currentSubscription } = useAuth();

    const [plan, setPlan] = useState<PlanSummary | null>(null);
    const [features, setFeatures] = useState<FeatureMap>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [upgradeModal, setUpgradeModal] = useState<{
        open: boolean;
        featureKey: string | null;
        featureLabel: string | null;
        requiredPlan: RequiredPlanInfo | null;
    }>({
        open: false,
        featureKey: null,
        featureLabel: null,
        requiredPlan: null,
    });

    // Avoid duplicate fetches
    const fetchingRef = useRef(false);

    const fetchFeatures = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const data = await apiGet<{
                success: boolean;
                plan: PlanSummary | null;
                features: FeatureMap;
            }>('/features');
            setPlan(data.plan);
            setFeatures(data.features ?? {});
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load features';
            setError(message);
            // Default to empty map - all hasFeature() checks return false
            setFeatures({});
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    // Fetch when authenticated, and re-fetch when the subscription plan changes
    useEffect(() => {
        if (token) {
            fetchFeatures();
        } else {
            setPlan(null);
            setFeatures({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, currentSubscription?.plan_key]);

    /* ── Local feature checks (from cached map) ─────────────────── */

    const hasFeature = useCallback(
        (key: string): boolean => {
            const value = features[key];
            if (value === undefined || value === null) return false;
            if (typeof value === 'boolean') return value;
            if (typeof value === 'number') return value === -1 || value > 0;
            if (typeof value === 'string') return value.trim().length > 0;
            return false;
        },
        [features],
    );

    const getFeatureValue = useCallback(
        (key: string): FeatureValue => {
            return features[key] ?? null;
        },
        [features],
    );

    const getFeatureLimit = useCallback(
        (key: string): number => {
            const value = features[key];
            if (typeof value === 'number') return value;
            return 0;
        },
        [features],
    );

    const getFeatureText = useCallback(
        (key: string): string | null => {
            const value = features[key];
            if (typeof value === 'string') return value;
            return null;
        },
        [features],
    );

    const featureValueContains = useCallback(
        (key: string, option: string): boolean => {
            const text = getFeatureText(key);
            if (!text) return false;
            const options = text.split(',').map((o) => o.trim());
            return options.includes(option);
        },
        [getFeatureText],
    );

    const featureLimitReached = useCallback(
        (key: string, currentCount: number): boolean => {
            const limit = getFeatureLimit(key);
            if (limit === -1) return false; // unlimited
            return currentCount >= limit;
        },
        [getFeatureLimit],
    );

    /* ── Upgrade modal ───────────────────────────────────────────── */

    const showUpgradeModal = useCallback(
        async (featureKey: string, featureLabel?: string) => {
            // Try to fetch the required plan for a richer modal
            let requiredPlan: RequiredPlanInfo | null = null;
            try {
                const res = await apiGet<RequiredPlanResponse>(
                    `/features/required-plan?key=${encodeURIComponent(featureKey)}`,
                );
                requiredPlan = res.required_plan;
            } catch {
                // Non-fatal - modal still opens without required plan
            }

            setUpgradeModal({
                open: true,
                featureKey,
                featureLabel: featureLabel ?? null,
                requiredPlan,
            });
        },
        [],
    );

    const closeUpgradeModal = useCallback(() => {
        setUpgradeModal((prev) => ({ ...prev, open: false }));
    }, []);

    /* ── Server-side authoritative checks ────────────────────────── */

    const checkFeature = useCallback(async (key: string): Promise<FeatureCheckResponse> => {
        return apiGet<FeatureCheckResponse>(`/features/check?key=${encodeURIComponent(key)}`);
    }, []);

    const checkLimit = useCallback(
        async (key: string, current: number): Promise<FeatureLimitResponse> => {
            return apiGet<FeatureLimitResponse>(
                `/features/limit?key=${encodeURIComponent(key)}&current=${current}`,
            );
        },
        [],
    );

    const checkContains = useCallback(
        async (key: string, option: string): Promise<FeatureContainsResponse> => {
            return apiGet<FeatureContainsResponse>(
                `/features/contains?key=${encodeURIComponent(key)}&option=${encodeURIComponent(option)}`,
            );
        },
        [],
    );

    const fetchRequiredPlan = useCallback(
        async (key: string): Promise<RequiredPlanResponse> => {
            return apiGet<RequiredPlanResponse>(
                `/features/required-plan?key=${encodeURIComponent(key)}`,
            );
        },
        [],
    );

    const value: FeatureAccessContextValue = {
        plan,
        features,
        loading,
        error,
        refresh: fetchFeatures,
        hasFeature,
        getFeatureValue,
        getFeatureLimit,
        getFeatureText,
        featureValueContains,
        featureLimitReached,
        showUpgradeModal,
        closeUpgradeModal,
        upgradeModal,
        checkFeature,
        checkLimit,
        checkContains,
        fetchRequiredPlan,
    };

    return (
        <FeatureAccessContext.Provider value={value}>{children}</FeatureAccessContext.Provider>
    );
}

/* ------------------------------------------------------------------ */
/* Hook                                                               */
/* ------------------------------------------------------------------ */

export function useFeatureAccess(): FeatureAccessContextValue {
    const context = useContext(FeatureAccessContext);
    if (context === undefined) {
        throw new Error('useFeatureAccess must be used within a FeatureAccessProvider');
    }
    return context;
}
